import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  inject,
  Signal,
  signal,
  WritableSignal
} from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';

import { CompanyIndustriesListComponent } from '@app/components/company/company-industries/company-industries-list/company-industries-list.component';
import { UiButtonComponent } from '@app/shared/ui/button/ui-button.component';

import { PaginationComponent } from '@app/shared/components/pagination/pagination.component';
import { ActiveCompanyService } from '@app/shared/services/active-company.service';
import { CompanyIndustriesDataService } from '@app/shared/services/company-industries-data.service';
import { DialogService } from '@app/shared/services/shared/dialog.service';
import { SortDirection } from '@app/shared/types/enums/sort-direction.enum';
import { Company } from '@app/shared/types/models/company/company.model';
import { Industry } from '@app/shared/types/models/industry/industry.model';
import { UiInputComponent } from '@app/shared/ui/input/ui-input.component';
import { UiPageWrapperComponent } from '@app/shared/ui/page-wrapper/ui-page-wrapper.component';
import { UiSpinnerDirective } from '@app/shared/ui/spinner/ui-spinner.directive';
import { TranslatePipe } from '@ngx-translate/core';

@Component({
  selector: 'proffeo-industries',
  templateUrl: './company-industries-view.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    UiPageWrapperComponent,
    TranslatePipe,
    UiInputComponent,
    CompanyIndustriesListComponent,
    PaginationComponent,
    UiButtonComponent,
    UiSpinnerDirective
  ]
})
export class CompanyIndustriesViewComponent {
  private readonly dialogService: DialogService = inject(DialogService);
  private readonly activeCompanyService: ActiveCompanyService = inject(ActiveCompanyService);
  private readonly companyIndustriesDataService: CompanyIndustriesDataService = inject(CompanyIndustriesDataService);
  private readonly queryParams = computed(() => ({
    page: this.page(),
    limit: this.limit(),
    search: this.searchValue(),
    sortField: this.sortField(),
    sortDir: this.sortDir(),
    companyId: this.activeCompany()?.id
  }));
  private destroyRef: DestroyRef = inject(DestroyRef);

  protected readonly getCompanyIndustriesPageList = computed(() =>
    this.companyIndustriesDataService.getCompanyIndustriesPageList(this.queryParams())()
  );

  protected searchValue: WritableSignal<string> = signal(undefined);
  protected page: WritableSignal<number> = signal(0);
  protected limit: WritableSignal<number> = signal(25);
  protected sortDir: Signal<SortDirection> = signal(SortDirection.ASC);
  protected sortField: Signal<string> = signal('name');
  protected activeCompany: Signal<Company | undefined> = this.activeCompanyService.activeCompany;

  public constructor() {
    toObservable(this.queryParams)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(params => {
        if (params.companyId) {
          this.companyIndustriesDataService.loadCompanyIndustriesPages(params);
        }
      });
  }

  protected openCreateCompanyIndustryDialog(): void {
    this.dialogService.openCreateCompanyIndustryDialog(this.queryParams());
  }

  protected deleteCompanyIndustry(companyIndustry: Industry): void {
    this.companyIndustriesDataService.deleteCompanyIndustry(this.queryParams(), companyIndustry);
  }
}
